import React, { useState, useEffect, useContext, useRef } from 'react';

import useTranslate from '../../../../../common/hooks/useTranslate';
import { useDispatch, useSelector } from 'react-redux';

import { getUserActivityFeed } from '../../../../services/userService';

import FilterCtx from '../../../../../context/filterContext/filterCtx';
import { RootState } from '../../../../../redux/store';
import { POPUP } from '../../../../../redux/types';

import { ISort } from '../../../types/ISort';

import { Grid, GridColumn } from '@progress/kendo-react-grid';
import HeaderCell from '../../../../documents/components/cells/headerCell/headerCell';
import DateCell from '../../../../documents/components/cells/dateCell/dateCell';
import ChipsCell from '../../../../documents/components/cells/chipsCell/chipsCell';
import ActionsCell from '../cells/actionsCell/actionsCell';
import ActivityTableFilters from '../filters/activityTableFilters';
import TableLoading from '../../../../ui/loaders/tableLoader/tableLoading';
import TextCell from '../../../../ui/table/cells/textCell/textCell';
import DocumentNameCell from '../../../../documents/components/cells/documentNameCell/documentNameCell';
import BoardNameCell from '../cells/boardNameCell/boardNameCell';
import MobileNameCell from '../../../../documents/components/cells/mobileNameCell/mobileNameCell';
import Icon from '../../../../ui/icon/icon';

import '@progress/kendo-theme-default/dist/all.css';
import './activityTable.scss';

const initialSort: ISort[] = [{ field: 'publishDate', dir: 'desc' }];

const ActivityTable = () => {
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [total, setTotal] = useState<number>(0);
  const [skip, setSkip] = useState<number>(0);
  const [sort, setSort] = useState<any>(initialSort);
  const [screenSize, setScreenSize] = useState<number>(0);
  const [collapsed, setCollapsed] = useState<boolean>(false);

  const { t } = useTranslate();
  const filterCtx = useContext(FilterCtx);
  const dispatch = useDispatch();
  const isFetching = useRef(false);
  const tableRef = useRef(null);

  const impersonate = useSelector((state: RootState) => state.impersonate);

  const pageSize = 100;
  const take = pageSize * 3;

  const ItemNameCell = (props) =>
    props.dataItem.type === 'board' ?
      <BoardNameCell {...props} icon={<Icon name='fal fa-tachometer-alt-average' className='document-icon' />} /> :
      <DocumentNameCell {...props} icon={<Icon name='fal fa-file-chart-line' className='document-icon' />}
                        origin='homePage' idField={'assignId'} />;
  const GridMobileNameCell = (props) => <MobileNameCell {...props} origin='homePage' idField={'assignId'} />;
  const TextCellAt = (props) => <TextCell useTooltip={true} {...props} />;
  const ActCell = (props) => <ActionsCell {...props} />;

  const fetchActivityFeed = (startResult, includeCount, sortField?, sortDir?) => {
    if (isFetching.current) return Promise.resolve();

    isFetching.current = true;
    setLoading(true);

    const start = Math.max(startResult - pageSize, 0);

    let type: string = null;

    if (!filterCtx.typePickerDocument && filterCtx.typePickerBoard) {
      type = 'board';
    } else if (!filterCtx.typePickerBoard && filterCtx.typePickerDocument) {
      type = 'doc';
    }

    return getUserActivityFeed(
      type,
      filterCtx.docNameActivity,
      filterCtx.fundsActivity,
      filterCtx.investorsActivity,
      filterCtx.classificationsActivity,
      filterCtx.activityFeedFromDate,
      start,
      take,
      sortField,
      sortDir,
      includeCount,
    )
      .then((results) => {
        generateDocuments(results, start, includeCount);
      })
      .catch((err) => dispatch({ type: POPUP.SET_SERVER_ERROR, payload: err.message }))
      .finally(() => {
        isFetching.current = false;
        setLoading(false);
      });
  };

  const generateDocuments = (results, startResult, includeCount) => {
    let totalCount = includeCount ? results.totalCount : total;
    const docs = documents.length === totalCount ? [...documents] : new Array(totalCount).fill({});
    results.data.forEach((doc, i) => {
      docs[i + startResult] = doc;
    });
    if (includeCount) setTotal(results.totalCount);
    setDocuments(docs);
  };

  const fetchRequired = (startResult) => {
    for (let i = startResult; i < startResult + pageSize && i < documents.length; i++) {
      if (!documents[i].assignId && !documents[i].contentId) {
        fetchActivityFeed(startResult, false, sort[0].field, sort[0].dir);
        break;
      }
    }
  };

  const handlePageChange = (event) => {
    let start = !event.page.skip || Number.isNaN(event.page.skip) ? 0 : event.page.skip;
    fetchRequired(start);
    setSkip(start);
  };

  const handleSortChange = (e) => {
    setSort(e.sort);
    documents.fill({});
    fetchActivityFeed(skip, false, e.sort[0].field, e.sort[0].dir);
  };

  useEffect(() => {
    setScreenSize(screen.width);
    fetchActivityFeed(0, true, sort[0].field, sort[0].dir);
  }, [filterCtx, impersonate]);

  return (
    <>
      <div className={'activity-feed-wrapper section'}>
        <div className='collapsable-section-title'>
          <span className='section-title'>{t('ACTIVITY.SECTION_TITLE')}</span>
          <Icon name={`far fa-${collapsed ? 'plus' : 'minus'}`}
                className={`collapse-icon pointer ${!collapsed ? 'minus' : ''}`} onClick={() => {
            setCollapsed(!collapsed);
          }} />
        </div>
        {<div className={`table activity-table ${collapsed ? 'collapsed-table' : ''}`}>
          <div className='activity-table-filters'>
            <ActivityTableFilters />
          </div>
          <Grid
            data={documents.slice(skip, skip + pageSize)}
            pageSize={pageSize}
            skip={skip}
            total={total}
            scrollable={'virtual'}
            sortable={{ allowUnsort: false }}
            sort={sort}
            rowHeight={40}
            dataItemKey={'id'}
            resizable={true}
            ref={tableRef}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}>
            <GridColumn field='label' title={t('NAME')} headerCell={HeaderCell}
                        cell={screenSize < 600 ? GridMobileNameCell : ItemNameCell} minResizableWidth={180} />
            <GridColumn field='publishDate' title={t('DOCUMENTS_TABLE.COLUMN_POSTED')}
                        cell={(props) => <DateCell {...props} />}
                        headerCell={HeaderCell} minResizableWidth={120} />
            <GridColumn
              field='classifications'
              title={t('DOCUMENTS_TABLE.COLUMN_CLASSIFICATIONS')}
              headerCell={HeaderCell}
              cell={(props) => <ChipsCell useTooltip={true} {...props} />}
              sortable={false}
              minResizableWidth={180}
            />
            <GridColumn field='fundName' title={t('DOCUMENTS_TABLE.COLUMN_FUND')} headerCell={HeaderCell}
                        cell={TextCellAt} minResizableWidth={180} />
            <GridColumn field='objectName' title={t('DOCUMENTS_TABLE.COLUMN_INVESTOR')} headerCell={HeaderCell}
                        cell={TextCellAt} minResizableWidth={180} />
            <GridColumn
              field='pin'
              title={t('DOCUMENTS_TABLE.COLUMN_ACTIONS')}
              headerCell={HeaderCell}
              cell={ActCell}
              sortable={false}
              width={100}
              minResizableWidth={100}
            />
          </Grid>
          {loading && <TableLoading tableElement={tableRef} />}
        </div>}
      </div>
    </>
  );
};

export default ActivityTable;
